import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/site/Layout";
import PageTracker from "@/components/site/PageTracker";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Industries from "@/pages/Industries";
import IndustryDetail from "@/pages/IndustryDetail";
import Solutions from "@/pages/Solutions";
import ProductDetail from "@/pages/ProductDetail";
import Resources from "@/pages/Resources";
import Manufacturers from "@/pages/Manufacturers";
import Contact from "@/pages/Contact";
import RequestQuote from "@/pages/RequestQuote";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import PartnerPortal from "@/pages/PartnerPortal";
import ProcurementBrief from "@/pages/ProcurementBrief";
import MatEconomicsBrief from "@/pages/MatEconomicsBrief";
import TCOFramework from "@/pages/TCOFramework";
import ValueMatrix from "@/pages/ValueMatrix";
import TechnicalDataSheet from "@/pages/TechnicalDataSheet";
import AssetManagerBrief from "@/pages/AssetManagerBrief";
import SafetyBrief from "@/pages/SafetyBrief";
import ScheduleBrief from "@/pages/ScheduleBrief";
import EngineeringBrief from "@/pages/EngineeringBrief";
import SourcingBrief from "@/pages/SourcingBrief";
import ROICalculator from "@/pages/ROICalculator";
import Battlecard from "@/pages/Battlecard";
import CaseStudy from "@/pages/CaseStudy";
import SustainabilityBrief from "@/pages/SustainabilityBrief";
import WarrantyBrief from "@/pages/WarrantyBrief";
import VerticalBrief from "@/pages/VerticalBrief";
import ProductValueMatrix from "@/pages/collateral/ProductValueMatrix";
import ProductROIBrief from "@/pages/collateral/ProductROIBrief";
import ProductDataSheet from "@/pages/collateral/ProductDataSheet";
import ProductBattlecard from "@/pages/collateral/ProductBattlecard";
import ProductCaseStudy from "@/pages/collateral/ProductCaseStudy";
import ProductComplianceBrief from "@/pages/collateral/ProductComplianceBrief";
import ProductROICalculator from "@/pages/collateral/ProductROICalculator";
import GroundProtectionFlyer from "@/pages/GroundProtectionFlyer";
import GroundProtectionDeck from "@/pages/GroundProtectionDeck";
import LandStewardship from "@/pages/LandStewardship";
import ResourcePreservationFlyer from "@/pages/ResourcePreservationFlyer";
import LandStewardshipDeck from "@/pages/LandStewardshipDeck";
import PinkwaterMarketingFlyer from "@/pages/PinkwaterMarketingFlyer";
import PinkwaterEducationalFlyer from "@/pages/PinkwaterEducationalFlyer";
import PinkwaterWhitePaper from "@/pages/PinkwaterWhitePaper";
import MatSelector from "@/pages/MatSelector";
import GroundProtection from "@/pages/GroundProtection";
import CategoryPage from "@/pages/CategoryPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import PremiumMats from "@/pages/PremiumMats";

function RequireAdmin({ children }) {
  const token = localStorage.getItem("oh_token");
  return token ? children : <Navigate to="/login" replace />;
}


function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <PageTracker />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/industries/:slug" element={<IndustryDetail />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/absorbents" element={<CategoryPage slug="absorbents" />} />
              <Route path="/water-treatment" element={<CategoryPage slug="water-treatment" />} />
              <Route path="/products/:slug" element={<ProductDetail />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/manufacturers" element={<Manufacturers />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/request-quote" element={<RequestQuote />} />
              <Route path="/ground-protection" element={<GroundProtection />} />
              <Route path="/mat-catalog" element={<Navigate to="/ground-protection" replace />} />
              <Route path="/land-stewardship" element={<LandStewardship />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/premium-mats" element={<PremiumMats />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/partnerportal" element={<PartnerPortal />} />
            <Route path="/big-bad-mats-procurement-brief" element={<ProcurementBrief />} />
            <Route path="/big-bad-mats-roi" element={<MatEconomicsBrief />} />
            <Route path="/big-bad-mats-tco" element={<TCOFramework />} />
            <Route path="/big-bad-mats-value-matrix" element={<ValueMatrix />} />
            <Route path="/big-bad-mats-datasheet" element={<TechnicalDataSheet />} />
            <Route path="/big-bad-mats-asset-roi" element={<AssetManagerBrief />} />
            <Route path="/big-bad-mats-safety" element={<SafetyBrief />} />
            <Route path="/big-bad-mats-schedule" element={<ScheduleBrief />} />
            <Route path="/big-bad-mats-engineering" element={<EngineeringBrief />} />
            <Route path="/big-bad-mats-sourcing" element={<SourcingBrief />} />
            <Route path="/big-bad-mats-calculator" element={<ROICalculator />} />
            <Route path="/big-bad-mats-battlecard" element={<Battlecard />} />
            <Route path="/big-bad-mats-case-study" element={<CaseStudy />} />
            <Route path="/big-bad-mats-sustainability" element={<SustainabilityBrief />} />
            <Route path="/big-bad-mats-warranty" element={<WarrantyBrief />} />
            <Route path="/big-bad-mats-industry/:slug" element={<VerticalBrief />} />
            <Route path="/collateral/:product/value-matrix" element={<ProductValueMatrix />} />
            <Route path="/collateral/:product/roi" element={<ProductROIBrief />} />
            <Route path="/collateral/:product/datasheet" element={<ProductDataSheet />} />
            <Route path="/collateral/:product/battlecard" element={<ProductBattlecard />} />
            <Route path="/collateral/:product/case-study" element={<ProductCaseStudy />} />
            <Route path="/collateral/:product/compliance" element={<ProductComplianceBrief />} />
            <Route path="/collateral/:product/calculator" element={<ProductROICalculator />} />
            <Route path="/ground-protection-flyer" element={<GroundProtectionFlyer />} />
            <Route path="/ground-protection-deck" element={<GroundProtectionDeck />} />
            <Route path="/resource-preservation-flyer" element={<ResourcePreservationFlyer />} />
            <Route path="/land-stewardship-deck" element={<LandStewardshipDeck />} />
            <Route path="/pinkwater-fire-marketing-flyer" element={<RequireAdmin><PinkwaterMarketingFlyer /></RequireAdmin>} />
            <Route path="/pinkwater-educational-flyer" element={<RequireAdmin><PinkwaterEducationalFlyer /></RequireAdmin>} />
            <Route path="/pinkwater-white-paper" element={<RequireAdmin><PinkwaterWhitePaper /></RequireAdmin>} />
          <Route path="/mat-selector" element={<MatSelector />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
